import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Hva slags digital meny trenger restauranten din?",
  },
  {
    id: "definition",
    label: "Hva betyr sammenligning av digitale menyer?",
  },
  {
    id: "criteria",
    label: "De viktigste kriteriene i sammenligningen",
  },
  {
    id: "provider-types",
    label: "Hvilke typer leverandører av digitale menyer finnes?",
  },
  {
    id: "pricing",
    label: "Sammenlign kostnader: gratis, billig eller nyttig på lang sikt?",
  },
  {
    id: "features",
    label: "Hvilke funksjoner betyr virkelig noe?",
  },
  {
    id: "seo",
    label: "Hvorfor synlighet og restaurantside betyr noe i sammenligningen",
  },
  {
    id: "mistakes",
    label: "Vanlige feil når man sammenligner digitale menyer",
  },
  {
    id: "bestplace",
    label: "Hvor Best Place passer inn i denne sammenligningen",
  },
  {
    id: "faq",
    label: "FAQ om sammenligning av digitale menyer",
  },
];

const checklist = [
  "Du sammenligner flere leverandører av digitale menyer.",
  "Du vil ikke velge bare ut fra laveste pris.",
  "Du trenger QR-kode, online meny eller mobil menytilgang.",
  "Du vil redigere priser, bilder og tilgjengelighet selv.",
  "Du kan trenge reservasjon eller bestillingsfunksjoner senere.",
  "Du vil vite om en gratis plan er nok å starte med.",
  "Du trenger en løsning som fungerer godt for gjester på smarttelefon.",
  "Du vil være mindre avhengig av PDF-er, trykking eller manuelle oppdateringer.",
  "Du vil koble restaurantside, digital meny og QR-kode på lang sikt.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "En veldig enkel leverandør kan være nok",
      text:
        "Hvis du bare vil vise en liten meny online, kan en enkel QR- eller menyløsning være nok. Sørg likevel for at menyen fungerer godt på smarttelefon.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Ikke sammenlign bare etter pris",
      text:
        "For restauranten din betyr flere kriterier noe: redigering, QR-kode, mobilvisning, bilder, tilgjengelighet og mulige fremtidige utvidelser.",
      tone: "medium",
    };
  }

  return {
    title: "Du trenger sannsynligvis en løsning som kan utvides",
    text:
      "Flere punkter viser at restauranten din bør velge en leverandør som gjør mer enn å vise en meny: administrasjon, QR-kode, nettside, reservasjoner og fremtidige utvidelser betyr noe.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalMenuComparisonNo() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Sammenligning av digitale menytilbydere | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/digitale-speisekarte-vergleich",
          en: "/en/digital-menu-comparison",
          no: "/no/digital-meny-sammenligning",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Sammenligning av digitale menytilbydere: hva restauranter virkelig
              bør se etter
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Den beste digitale menyen er ikke automatisk den billigste, og
                heller ikke den med den lengste funksjonslisten. Det viktigste er
                om den hjelper i ekte restaurantarbeid: menyoppdateringer,
                QR-tilgang, prisendringer, gjesteinformasjon og fremtidig vekst.
              </p>

              <p className="seoHero__text">
                Denne guiden forklarer hvordan restauranter kan sammenligne
                leverandører av digitale menyer, hvilke kriterier som betyr noe,
                og hvorfor en digital meny bør være mer enn bare en PDF bak en
                QR-kode.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva slags digital meny trenger restauranten din?</h2>

            <p>
              Trykk på utsagnene som passer for restauranten din. Resultatet er
              bevisst ærlig: noen ganger er en enkel QR-meny nok, men mange
              restauranter trenger en løsning som kan vokse med menyadministrasjon,
              reservasjon, nettside og fremtidige funksjoner.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Hva betyr sammenligning av digitale menyer?</h2>

          <p>
            Å sammenligne digitale menyer bør ikke bare bety å spørre: “Hvilken
            leverandør er billigst?” Det bedre spørsmålet er: hvilken løsning
            passer restauranten, teamet, gjestene og de fremtidige planene dine?
          </p>

          <p>
            En digital meny kan bety flere ting: en PDF med QR-kode, en enkel
            mobil menyside eller en bredere restaurantplattform med meny,
            QR-kode, reservasjon, flerspråklig innhold og fremtidige
            bestillingsmuligheter.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Mobilvisning.</li>
            <li className="text__seo-li">QR-kode-tilgang.</li>
            <li className="text__seo-li">Enkel menyadministrasjon.</li>
            <li className="text__seo-li">Bilder og beskrivelser.</li>
            <li className="text__seo-li">Priser og tilgjengelighet.</li>
            <li className="text__seo-li">Flerspråklig støtte.</li>
            <li className="text__seo-li">Reservasjon.</li>
            <li className="text__seo-li">Nettside eller restaurantside.</li>
            <li className="text__seo-li">Fremtidige utvidelser.</li>
          </ul>

          <h3>Sammenlign arbeidsflyten, ikke bare prisen</h3>

          <p>
            Et billig verktøy kan bli frustrerende hvis menyen er vanskelig å
            oppdatere, QR-kode-flyten er uklar, eller gjester sliter på
            smarttelefon. En nyttig sammenligning bør alltid inkludere ekte
            restaurantarbeid.
          </p>
        </section>

        <section id="criteria" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>De viktigste kriteriene i sammenligningen</h2>

            <p>
              En god digital meny trenger ikke alle mulige funksjoner. Men den
              bør svare tydelig på de viktigste praktiske spørsmålene.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Kjernekriterier</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Mobilbruk:</strong> Åpner menyen raskt og tydelig på
                    en smarttelefon?
                  </li>

                  <li className="text__seo-li">
                    <strong>Menyadministrasjon:</strong> Kan priser, bilder og
                    tilgjengelighet endres enkelt?
                  </li>

                  <li className="text__seo-li">
                    <strong>QR-kode:</strong> Er QR-koden enkel å bruke, teste og
                    trykke?
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Vekstkriterier</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Design:</strong> Føles menyen profesjonell og
                    passende for restauranten?
                  </li>

                  <li className="text__seo-li">
                    <strong>Skalerbarhet:</strong> Kan reservasjon, nettside
                    eller bestilling legges til senere?
                  </li>

                  <li className="text__seo-li">
                    <strong>Pris:</strong> Er det tydelig hva som er gratis, og
                    når en oppgradering blir nødvendig?
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Den beste leverandøren er ikke alltid den med lengst
              funksjonsliste. Den beste leverandøren er den restauranten faktisk
              bruker.
            </p>
          </div>
        </section>

        <section id="provider-types" className="acquaintance">
          <h2>Hvilke typer leverandører av digitale menyer finnes?</h2>

          <p>
            Digitale menyer finnes i flere former. For å sammenligne rettferdig
            må du først forstå hvilken type løsning du ser på.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF med QR-kode</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Rask start.</li>
                <li className="text__seo-li">Ofte billig eller gratis.</li>
                <li className="text__seo-li">
                  Mindre behagelig på smarttelefon.
                </li>
                <li className="text__seo-li">
                  Vanskelig når endringer skjer ofte.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col">
              <h3>Enkel menyside</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Bedre mobilopplevelse.</li>
                <li className="text__seo-li">
                  Kategorier og produkter er mulig.
                </li>
                <li className="text__seo-li">Bra for små restauranter.</li>
                <li className="text__seo-li">Ofte begrensede utvidelser.</li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Restaurantplattform</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Meny, QR-kode og restaurantside er koblet sammen.
                </li>
                <li className="text__seo-li">
                  Reservasjon eller bestilling kan legges til senere.
                </li>
                <li className="text__seo-li">
                  Bedre for langsiktig utvikling.
                </li>
                <li className="text__seo-li">
                  Må fortsatt være enkel å bruke.
                </li>
              </ul>
            </div>
          </div>

          <VisualIdea>
            Tre sammenligningskort på skrivebordet til en restauranteier: PDF med
            QR-kode, enkel mobil menyside og full restaurantplattform.
          </VisualIdea>
        </section>

        <section id="pricing" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Sammenlign kostnader: gratis, billig eller nyttig på lang sikt?
            </h2>

            <p>
              Mange restauranter søker først etter en gratis digital meny. Det
              gir mening. En gratis plan kan være et sterkt utgangspunkt når du
              vil teste om QR-meny og online administrasjon fungerer i ekte
              daglig drift.
            </p>

            <p>
              Likevel er startprisen ikke det eneste som betyr noe. Det er også
              viktig hva som skjer når restauranten vokser eller trenger flere
              funksjoner.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Hva er gratis?</li>
              <li className="text__seo-li">Hva koster ekstra senere?</li>
              <li className="text__seo-li">Er QR-kode inkludert?</li>
              <li className="text__seo-li">Er menyadministrasjon inkludert?</li>
              <li className="text__seo-li">Er reservasjon mulig?</li>
              <li className="text__seo-li">Finnes det en restaurantside?</li>
              <li className="text__seo-li">
                Er flerspråklig innhold mulig?
              </li>
              <li className="text__seo-li">Kan systemet vokse senere?</li>
            </ul>

            <div className="seoCompareSmall">
              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/gratis-digital-meny-for-restauranter">
                    Gratis digital meny
                  </Link>
                </p>
              </div>

              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/administrere-meny-online-for-restauranter">
                    Administrere meny online
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>Hvilke funksjoner betyr virkelig noe?</h2>

          <p>
            Ikke alle funksjoner er viktige for alle restauranter. En liten kafé
            trenger kanskje først bare en QR-meny. En restaurant med reservasjoner,
            terrasseplasser og skiftende tilbud kan trenge mer struktur.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Vis menyen online.</li>
            <li className="text__seo-li">Bruk QR-kode.</li>
            <li className="text__seo-li">Rediger priser.</li>
            <li className="text__seo-li">Skjul retter.</li>
            <li className="text__seo-li">Legg til reservasjon.</li>
            <li className="text__seo-li">Utvid senere.</li>
          </ol>

          <p>
            Rekkefølgen betyr noe: grunnlaget må fungere først. Deretter kan
            flere funksjoner legges til med et tydelig formål.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/digital-meny-for-restauranter">
                  Digital meny
                </Link>
              </p>
            </div>

            <div>
              <strong>Relatert side</strong>
              <p>
                <Link to="/no/online-bordreservasjon-for-restauranter">
                  Online bordreservasjon
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="seo" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Hvorfor synlighet og restaurantside betyr noe i sammenligningen
            </h2>

            <p>
              En digital meny er ikke bare viktig for gjester ved bordet. Den kan
              også hjelpe før besøket: når gjester søker online, sjekker priser,
              ser bilder eller bestemmer seg for å reservere.
            </p>

            <p>
              Derfor bør sammenligningen inkludere ett spørsmål til: tilbyr
              leverandøren bare en menylenke, eller kan løsningen bli en del av
              en bedre digital tilstedeværelse for restauranten?
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Bare menylenke</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Nyttig hovedsakelig etter at gjesten har QR-koden.
                  </li>
                  <li className="text__seo-li">
                    Begrenset støtte for synlighet før besøket.
                  </li>
                  <li className="text__seo-li">
                    Restaurantinformasjon kan forbli spredt.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Meny + restaurantside</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Menyen kan støtte online synlighet.
                  </li>
                  <li className="text__seo-li">
                    Restaurantinformasjon blir tydeligere.
                  </li>
                  <li className="text__seo-li">
                    Reservasjon og kontakt kan kobles sammen.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/restaurant-nettside">
                    Restaurantnettside
                  </Link>
                </p>
              </div>

              <div>
                <strong>Relatert side</strong>
                <p>
                  <Link to="/no/restaurant-digitalisering">
                    Restaurantdigitalisering
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Vanlige feil når man sammenligner digitale menyer</h2>

          <p>
            Sammenligning er bare nyttig når den er praktisk. En leverandør kan
            se bra ut i en pristabell, men likevel passe dårlig for ekte
            restaurantarbeid.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Vanlige feil</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Man ser bare på laveste pris.
                </li>
                <li className="text__seo-li">
                  Man forveksler en PDF med en ekte digital meny.
                </li>
                <li className="text__seo-li">
                  Mobilvisningen testes ikke.
                </li>
                <li className="text__seo-li">
                  Menyadministrasjon undervurderes.
                </li>
                <li className="text__seo-li">
                  Man stiller ikke spørsmål om fremtiden.
                </li>
                <li className="text__seo-li">
                  Man stoler på anmeldelser uten å teste verktøyet.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Test arbeidsflyten med din egen meny.
                </li>
                <li className="text__seo-li">
                  Sjekk opplevelsen på smarttelefon.
                </li>
                <li className="text__seo-li">
                  Endre en pris og skjul en rett.
                </li>
                <li className="text__seo-li">
                  Sjekk QR-kode og restaurantside.
                </li>
                <li className="text__seo-li">
                  Spør om reservasjon eller bestilling kan legges til senere.
                </li>
                <li className="text__seo-li">
                  Sammenlign nytteverdi, ikke bare funksjonslister.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om sammenligning av digitale menyer</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva er den beste digitale menyen?</summary>

              <p>
                Den beste løsningen avhenger av restauranten. En liten kafé
                trenger andre funksjoner enn en restaurant med reservasjoner,
                terrasseplasser, flere språk eller skiftende tilbud.
              </p>
            </details>

            <details>
              <summary>Er en gratis leverandør nok?</summary>

              <p>
                En gratis leverandør kan være nok i starten. Det viktige
                spørsmålet er om du realistisk kan teste menyadministrasjon,
                QR-kode-tilgang og mobilvisning.
              </p>
            </details>

            <details>
              <summary>Er en PDF med QR-kode nok?</summary>

              <p>
                For en veldig enkel start kan det være nok. Hvis du vil oppdatere
                priser, bilder, dagstilbud eller tilgjengelighet, er en
                redigerbar online meny vanligvis bedre.
              </p>
            </details>

            <details>
              <summary>Hva bør jeg sjekke først?</summary>

              <p>
                Mobilvisning, enkel redigering, QR-kode, aktuelle priser, bilder,
                tilgjengelighet og fremtidig skalerbarhet er spesielt viktig.
              </p>
            </details>

            <details>
              <summary>Trenger jeg en stor plattform med en gang?</summary>

              <p>
                Nei. Det er ofte bedre å starte lite. Men løsningen bør kunne
                vokse hvis reservasjon, flerspråklig innhold eller bestilling
                blir viktig senere.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="business"
          exclude="/no/digital-meny-sammenligning"
        />
      </article>
    </main>
  );
}